import { useEffect, useState } from 'react';
import Modal from 'components/modal/Modal';
import {
  CardHeader,
  CardHeaderTitle,
  CardHeaderSubtitle
} from 'components/card/card-header';
import { FormattedMessage, useIntl } from 'react-intl';
import { CardBody, CardFooter } from 'components/card';
import CardBodyTools from 'components/card/CardBodyTools';
import { Button, ButtonsGroup } from 'components/button';
import messages from 'messages';
import Loader from 'components/loader/Loader';
import ListBody from 'components/list/ListBody';
import TemplatesListItem from 'containers/partials/list-items/TemplatesListItem';
import List from 'components/list/List';
import TemplatesListItemForm from 'containers/partials/list-items/TemplatesListItemForm';
import useSelectable from 'hooks/utils/useSelectable';
import Message from 'components/message/Message';
import MessageText from 'components/message/MessageText';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import PropTypes from 'prop-types';
import FieldInput from 'components/input/FieldInput';
import { TemplateType } from 'constants.js';
import useTemplates from 'hooks/queries/useTemplates';
import { useStore } from 'stores/Store';
import { useMutation } from '@apollo/client';
import { MUTATION_ADD_EXERCISES_TO_TEMPLATES } from 'services/aws/templates-query';
import { useNotificationQueue } from 'components/notification';

const AddExercisesToCollectionModal = ({
  entityId,
  exercises,
  onAdded,
  onClose,
  collectionType
}) => {
  const intl = useIntl();
  const { fetchTemplates, filterTemplates, loading } = useTemplates(entityId);
  const templates = useStore(state => state.templates);
  const resetTemplatesFilter = useStore(state => state.resetTemplatesFilter);

  const ability = useAbility(AbilityContext);
  const [newTemplateId, setNewTemplateId] = useState(null);
  const [templatesList, setTemplatesList] = useState([]);
  const { selected, select } = useSelectable([]);
  const notification = useNotificationQueue();

  const [addExercises] = useMutation(MUTATION_ADD_EXERCISES_TO_TEMPLATES);

  const resetSelectedExercises = useStore(
    state => state.resetSelectedExercises
  );

  useEffect(() => {
    fetchTemplates({
      variables: { entityId, type: TemplateType[collectionType.toUpperCase()] }
    });
    return () => {
      resetTemplatesFilter();
    };
  }, []);

  useEffect(() => {
    if (templates) {
      const myTemplates = templates; //.filter(t => t.personId === user.id);
      if (newTemplateId) {
        const newTemplate = myTemplates.find(t => t.id === newTemplateId);
        const filteredTemplates = myTemplates.filter(
          t => t.id !== newTemplateId
        );
        filteredTemplates.unshift(newTemplate);
        setTemplatesList(filteredTemplates);
        return;
      }
      setTemplatesList(myTemplates);
    }
  }, [templates, newTemplateId]);

  const onCloseHandler = () => {
    resetSelectedExercises();
    if (onClose) onClose();
  };

  const onAddHandler = async () => {
    await addExercises({
      variables: {
        exerciseIds: exercises.map(e => e.id),
        programTemplateIds: selected
      }
    }).then(
      () => {
        notification.add('add_exercises', {
          message: intl.formatMessage(messages.messageExercisesAddedToTemplate)
        });
        resetSelectedExercises();
      },
      () => {
        notification.add('edit_error', {
          message: intl.formatMessage(
            messages.messageExercisesAddedToTemplateError
          ),
          level: 'error'
        });
      }
    );
    onAdded && onAdded();
  };

  const onSearch = e => {
    filterTemplates({ search: e.target.value });
  };

  const onCompleteTemplateHandler = async templateId => {
    select(templateId);
    await fetchTemplates({
      variables: { entityId, type: TemplateType[collectionType.toUpperCase()] },
      fetchPolicy: 'network-only'
    });
    setNewTemplateId(templateId);
  };

  return (
    <Modal
      card
      isOpen={true}
      onClose={onCloseHandler}
      extraClassNames={'c-card-modal__content-body-scroll'}
    >
      <CardHeader modal>
        <CardHeaderTitle>
          <FormattedMessage
            {...messages[`addExercisesTo${collectionType}ModalTitle`]}
          />
        </CardHeaderTitle>
        <CardHeaderSubtitle>
          <FormattedMessage
            {...messages[`addExercisesTo${collectionType}ModalSubtitle`]}
          />
        </CardHeaderSubtitle>
      </CardHeader>

      <CardBody modal bodyWithHeader>
        {loading && <Loader />}
        <CardBodyTools style={{ flexWrap: 'wrap' }}>
          <FieldInput
            id="title"
            rounded
            placeholder={intl.formatMessage(
              messages.exercisesProgramFilterNameLabel
            )}
            onChange={onSearch}
            icon="search-grey"
            iconLeft
          />
        </CardBodyTools>
        <List onModal>
          <ListBody
            style={{
              minHeight: 'calc(100% - 40px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Add a new Collection */}
            {!newTemplateId && (
              <TemplatesListItemForm
                extraClassNames="c-list__item-with-input"
                entityId={entityId}
                onComplete={onCompleteTemplateHandler}
                collectionType={collectionType}
              />
            )}

            {templatesList.map(
              template =>
                ability.can('update', template) && (
                  <TemplatesListItem
                    icon={'template'}
                    checkbox={ability.can('update', template)}
                    id={`modal_${template.id}`}
                    key={template.id}
                    template={template}
                    entityId={entityId}
                    visible
                    checked={selected.includes(template.id)}
                    // onClick={() => onClickHandler(template)}
                    // In this context, a modal, clicking on the element
                    // should also select the list item.
                    onClick={() => {
                      ability.can('update', template) && select(template.id);
                    }}
                    onChange={() =>
                      ability.can('update', template) && select(template.id)
                    }
                  />
                )
            )}

            {templatesList.length <= 0 && !loading && (
              <li className="u-padding-bottom-large u-margin-auto">
                <Message emptyState={true} icon="cursor">
                  <MessageText>
                    <FormattedMessage {...messages.templateListEmpty} />
                  </MessageText>
                </Message>
              </li>
            )}
          </ListBody>
        </List>
      </CardBody>

      <CardFooter modal>
        <ButtonsGroup>
          <Button secondary onClick={() => onCloseHandler()}>
            <FormattedMessage {...messages.cancelButton} />
          </Button>
          <Button
            disabled={selected.length === 0}
            primary
            onClick={() => onAddHandler()}
          >
            <FormattedMessage {...messages.addButton} />
          </Button>
        </ButtonsGroup>
      </CardFooter>
    </Modal>
  );
};

AddExercisesToCollectionModal.propTypes = {
  entityId: PropTypes.string.isRequired,
  collectionType: PropTypes.string.isRequired
};

export default AddExercisesToCollectionModal;
